import {
    Bounds, Logger, namespace, Point,
} from '../../../../../FrontendBundle/Resources/js/ofcs';
import Cell from '../grid/cell';
import Composite from '../grid/composite';
import Dimension from '../grid/dimension';
import Matrix from '../grid/matrix';
import ContainerItem from './containerItem';
import InsertItem from './insertItem';
import FoamItem from './foamItem';

namespace('Ofcs.Editor.Item');

/**
 * @class GridItem
 * @extends ContainerItem
 */
export default window.Ofcs.Editor.Item.GridItem = ContainerItem.extend({

    type: 'grid',

    supportsMultiChildMove: false,

    supportsMultiChildCopy: false,

    defaults: {
        /**
         * @property {string} Type
         */
        type: null,

        /**
         * Current position
         * @property {Point} attributes.position
         */
        position: new Point(0, 0),

        /**
         * Width of the grid
         * @property {number} attributes.width
         */
        width: 0,

        /**
         * Height of the grid
         * @property {number} attributes.height
         */
        height: 0,

        /**
         * Columns
         * @property {number} attributes.columns
         */
        columns: 0,

        /**
         * Rows
         * @property {number} attributes.rows
         */
        rows: 0,

        /**
         * Direction of the grid
         * @property {string} attributes.direction
         */
        direction: 'horizontal',

        /**
         * Cell identifier
         * @property {string} attributes.cellIdentifier
         */
        cellIdentifier: 'horizontal',

        /**
         * Cell bounds
         * @property {string} attributes.cellBounds
         */
        cellBounds: new Bounds(new Point(0, 0), new Point(39.3, 54.6)),

        /**
         * Cell image
         * @property {string} attributes.cellImage
         */
        cellImage: '',
    },

    /**
     * @constructor
     */
    initialize() {
        this.matrix = new Matrix(this.get('rows'), this.get('columns'));

        ContainerItem.prototype.initialize.call(this);

        this.getItems().on('add', this._onAdd, this);
        this.getItems().on('remove', this._onRemove, this);
        this.getItems().on('change', this._onChange, this);
    },

    getClassification() {
        return `${this.type}#${this.getParentItem().get('depth')}`;
    },

    /**
     * Called when item is added to grid
     * @param {Ofcs.Editor.Item.ItemInterface} item
     * @private
     */
    _onAdd(item) {
        let composite = this.itemToComposite(item);
        let cell;

        if (!item.get('position') || !composite || !this.matrix.isFree(composite)) {
            cell = this.getFreeCell(item);
        } else {
            cell = composite.getCell();
        }

        if (!cell) {
            Logger.warn('No free cell found for item', item);
            return;
        }

        this._fixPosition(item, cell);

        composite = this.itemToComposite(item);

        if (!composite) {
            Logger.warn('Composite could not be created from item', item);
            return;
        }

        this.matrix.mark(composite, item);
        this.matrix.dump();
    },

    /**
     * Called when item is removed from grid
     * @param {Ofcs.Editor.Item.ItemInterface} item
     * @private
     */
    _onRemove(item) {
        const composite = this.itemToComposite(item);

        if (!composite) {
            Logger.warn('Composite could not be created from item', item);
            return;
        }

        this.matrix.freeByContent(item);
        this.matrix.dump();
    },

    /**
     * Called when item position in grid changes
     * @param {ItemInterface} item
     * @param {Object} options
     * @private
     */
    _onChange(item) {
        const composite = this.itemToComposite(item);

        if (!composite) {
            Logger.warn('Composite could not be created from item', item);
            return;
        }

        this.matrix.freeByContent(item);
        this.matrix.mark(composite, item);
        this.matrix.dump();
    },

    /**
     * Converts a given item to a composite object
     * with respective cell and dimensions.
     *
     * @param {AbstractItem} item
     * @return {Composite}
     */
    itemToComposite(item) {
        const cell = this.itemToCell(item);
        return cell ? new Composite(cell, this.getItemDimensions(item)) : null;
    },

    /**
     * Reduces item position to the item's top
     * left cell iff inside of the grid matrix
     *
     * @param {AbstractItem} item
     * @return {Cell|null} returns cell if top left item cell lies within grid matrix, else null
     */
    itemToCell(item) {
        const cellWidth = this.getCellWidth();
        const cellHeight = this.getCellHeight();
        const itemTopLeft = item.get('position');

        if (!cellWidth || !cellHeight || !itemTopLeft) {
            return null;
        }

        const row = Math.round(itemTopLeft.y / cellHeight);
        const column = Math.round(itemTopLeft.x / cellWidth);

        if (row < 0 || column < 0 || row >= this.get('rows') || column >= this.get('columns')) {
            return null;
        }

        return new Cell(row, column);
    },

    /**
     * TODO: remove this helper function when proper item instantiation is implemented
     * --> is actually meant to run through  this.getItems().on('add') listener in initializer method
     *
     * @param {AbstractItem} item
     */
    addItem(item) {
        this.getItems().add(item);
    },

    /**
     * @param {AbstractItem} item
     * @param {Cell} cell
     * @private
     */
    _fixPosition(item, cell) {
        const itemTopLeftX = cell.getColumn() * this.getCellWidth();
        const itemTopLeftY = cell.getRow() * this.getCellHeight();

        item.set('position', new Point(itemTopLeftX, itemTopLeftY));
    },

    /**
     * Returns the first free cell when scanning
     * the grid matrix row by row from left to right
     *
     * @param {AbstractItem} item
     * @return {Cell|null} the top most left cell in the grid for the requested dimension iff free,
     *      else null
     */
    getFreeCell(item) {
        return this.matrix.getFreeCell(this.getItemDimensions(item));
    },

    /**
     * @param {Composite} minDimension
     * @return {Composite|null}
     */
    getLargestFreeArea(minDimension) {
        return this.matrix.getLargestFreeArea(minDimension);
    },

    /**
     * Checks if all contained cells are
     * currently free in the grid matrix
     *
     * @param {Cell} cell
     * @param {Dimension} dimension
     * @param {Ofcs.Editor.Item.InsertItem} item
     * @return {boolean}
     */
    isCellFree(cell, dimension, item) {
        const composite = new Composite(cell, dimension);
        return this.isAreaFree(composite, item);
    },

    /**
     * Checks if all contained cells are currently free in the grid matrix
     *
     * @param {Composite} composite
     * @param {InsertItem} item
     * @return {boolean}
     */
    isAreaFree(composite, item) {
        return this.matrix.isFree(composite, item);
    },

    /**
     * Checks if a composite is within the grid boundaries
     *
     * @param {Composite} composite
     *
     * @return {boolean}
     */
    isCompositeInsideGrid(composite) {
        return composite.cell.getColumn() >= 0
            && composite.dimensionCell.getColumn() <= this.get('columns')
            && composite.cell.getRow() >= 0
            && composite.dimensionCell.getRow() <= this.get('rows');
    },

    /**
     * Parses response data into attributes
     *
     * @param {Object} responseParam Response hash
     * @return {Object} Attribute hash
     */
    parse(responseParam) {
        const response = ContainerItem.prototype.parse.call(this, responseParam);

        if (response.cell.id !== undefined) {
            response.cellId = response.cell.id;
        }

        if (response.cell.identifier !== undefined) {
            response.cellIdentifier = response.cell.identifier;
        }

        if (response.cell.image !== undefined) {
            response.cellImage = response.cell.image;
        }

        if (response.cell.width !== undefined && response.cell.height !== undefined) {
            response.cellBounds = new Bounds(new Point(0, 0), new Point(response.cell.width, response.cell.height));
        }

        if (this.parentItem && this.parentItem instanceof Ofcs.Editor.Item.BoxItem) {
            response.position = this.parentItem.get('internalAreaPosition').clone();
        }

        delete response.cell;

        return response;
    },

    /**
     * Returns a new bounding box based on the current *attributes* of the item
     *
     * @return {Bounds} Bounding box of the item
     */
    getBounds() {
        const box = this.getParentItem();

        return new Bounds(
            box.get('internalAreaPosition').clone(),
            new Point(
                box.get('internalAreaPosition').x + this.get('width'),
                box.get('internalAreaPosition').y + this.get('height'),
            ),
        );
    },

    /**
     * Returns a new bounding box based on the current *attributes* of the item
     *
     * @return {Bounds} Bounding box of the item
     */
    getMarginBounds() {
        return this.getBounds();
    },

    /**
     * Returns a new bounding box based on the current *attributes* of the item
     *
     * @return {Bounds} Bounding box of the item
     */
    getInnerBounds() {
        return this.getBounds();
    },

    /**
     * Returns grid's cell width
     * @return {number}
     */
    getCellWidth() {
        const cellBounds = this.get('cellBounds');
        return this.get('direction') === 'horizontal' ? cellBounds.getWidth() : cellBounds.getHeight();
    },

    /**
     * Returns grid's cell height
     * @return {number}
     */
    getCellHeight() {
        const cellBounds = this.get('cellBounds');
        return this.get('direction') === 'horizontal' ? cellBounds.getHeight() : cellBounds.getWidth();
    },

    /**
     * Returns item's height in rows
     *
     * @param {AbstractItem} item
     * @return {number}
     */
    getItemRows(item) {
        return Math.ceil(item.get('height') / this.getCellHeight());
    },

    /**
     * Returns item's width in columns
     *
     * @param {AbstractItem} item
     * @return {number}
     */
    getItemColumns(item) {
        return Math.ceil(item.get('width') / this.getCellWidth());
    },

    /**
     * Returns new dimension object for given item
     *
     * @param {AbstractItem} item
     * @return {Dimension}
     */
    getItemDimensions(item) {
        const width = item.get('width');
        const height = item.get('height');
        const columns = Math.round(width / this.getCellWidth());
        const rows = Math.round(height / this.getCellHeight());

        if (rows < 1 || columns < 1) {
            return null;
        }

        return new Dimension(rows, columns);
    },

    /**
     * Returns new dimension object for given size
     *
     * @param {number} width - optional item width to override
     * @param {number} height - optional item height to override
     * @return {Dimension}
     */
    getSizeDimensions(width, height) {
        const columns = Math.round(width / this.getCellWidth());
        const rows = Math.round(height / this.getCellHeight());

        if (rows < 1 || columns < 1) {
            return null;
        }

        return new Dimension(rows, columns);
    },

    getLines() {
        return [];
    },

    /**
     * @param {AbstractItem} item
     * @return {boolean}
     */
    isItemSupported(item) {
        return item instanceof InsertItem
            || item instanceof FoamItem;
    },
});
